/**
 * 根据 URL / 会话 / 浏览器解析门户语言，并生成规范路径。
 */
import {
  getBrowserLanguageTags,
  matchBrowserLocale,
  resolveInitialLocale
} from '@/components/shared/utils/resolveBrowserLocale'
import {
  dropLangQuery,
  findDefaultUnprefixedLocale,
  isLocaleHomePath,
  localeToPrefix,
  parseLocalePrefix,
  prefixToLocale,
  readLangQuery,
  stripLocalePrefix,
  withLocalePrefix
} from '@/components/portal/common/utils/localePath'

export function isValidEnabledLocale(code, enabledLocales = []) {
  return Boolean(code && enabledLocales.some((item) => item.code === code))
}

function resolveHomeLocale({ enabled, sessionLocale }) {
  if (isValidEnabledLocale(sessionLocale, enabled)) {
    return { locale: sessionLocale, source: 'session' }
  }
  const browserTags = getBrowserLanguageTags()
  const browserMatch = matchBrowserLocale(browserTags, enabled)
  if (browserMatch) {
    return { locale: browserMatch, source: 'browser', browserTags }
  }
  const locale = resolveInitialLocale({
    saved: '',
    enabledLocales: enabled,
    browserTags: []
  })
  return {
    locale,
    source: locale ? 'fallback-en' : 'default',
    browserTags
  }
}

/**
 * @param {object} options
 * @param {string} options.path
 * @param {object} [options.query]
 * @param {Array} options.enabled
 * @param {string} [options.sessionLocale]
 * @param {string} [options.currentLocale]
 * @param {boolean} [options.isInitial]
 */
export function resolveLocaleForVisit({
  path,
  query = {},
  enabled = [],
  sessionLocale = '',
  currentLocale = '',
  isInitial = true
}) {
  const prefix = parseLocalePrefix(path)
  const prefixLocale = prefix ? prefixToLocale(prefix, enabled) : ''
  if (isValidEnabledLocale(prefixLocale, enabled)) {
    return { locale: prefixLocale, source: 'path' }
  }

  const langQuery = readLangQuery(query)
  if (isValidEnabledLocale(langQuery, enabled)) {
    return { locale: langQuery, source: 'query' }
  }

  const home = isLocaleHomePath(path)

  if (isInitial) {
    if (home) return resolveHomeLocale({ enabled, sessionLocale })
    return {
      locale: findDefaultUnprefixedLocale(enabled),
      source: 'default-zh'
    }
  }

  if (isValidEnabledLocale(currentLocale, enabled)) {
    return { locale: currentLocale, source: 'current' }
  }
  if (home) return resolveHomeLocale({ enabled, sessionLocale })
  return {
    locale: findDefaultUnprefixedLocale(enabled),
    source: 'default-zh'
  }
}

export function buildCanonicalPath(path, locale) {
  return withLocalePrefix(stripLocalePrefix(path), locale)
}

function isPrefixStackedPath(currentPath, canonicalPath, locale) {
  const prefix = localeToPrefix(locale)
  if (!prefix) return false
  const normalized = currentPath.startsWith('/') ? currentPath : `/${currentPath}`
  const head = `/${prefix}`
  const alreadyPrefixed = normalized === head || normalized.startsWith(`${head}/`)
  if (!alreadyPrefixed) return false
  return canonicalPath === `${head}${normalized}`
}

export function countRedirectChain(route) {
  let count = 0
  let current = route
  while (current?.redirectedFrom) {
    count += 1
    current = current.redirectedFrom
    if (count > 2) return count
  }
  return count
}

export function needsCanonicalRedirect(to, locale) {
  if (!to || !locale) return null
  const currentPath = String(to.path || '')
  const canonicalPath = buildCanonicalPath(currentPath, locale)
  const nextQuery = dropLangQuery(to.query || {})
  const langDropped = Boolean(readLangQuery(to.query))
  const pathChanged = currentPath !== canonicalPath
  if (!pathChanged && !langDropped) return null

  if (pathChanged && isPrefixStackedPath(currentPath, canonicalPath, locale)) {
    if (!langDropped) return null
    return {
      path: currentPath,
      query: nextQuery,
      hash: to.hash || '',
      replace: true
    }
  }

  return {
    path: canonicalPath,
    query: nextQuery,
    hash: to.hash || '',
    replace: true
  }
}
